import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  getSettings,
  getProductSettings,
  updateProductSetting,
  deleteProductSetting,
  GlobalSettings,
  ProductSettings,
  ShopifyProduct,
} from '../../services/adminApi';
import { getProducts } from '../../services/api';

/**
 * Admin Products Page
 *
 * Manage per-product wholesale settings:
 * - Hide/show products from catalog
 * - Override discount percentages
 * - Override quantity discounts
 * - Set minimum quantities
 */
export default function AdminProducts() {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [productSettings, setProductSettings] = useState<Map<string, ProductSettings>>(new Map());
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingProduct, setEditingProduct] = useState<string | null>(null);
  const [savingProduct, setSavingProduct] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [showOnlyCustom, setShowOnlyCustom] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'removed'>('all');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch products, global settings, and product settings in parallel
      const [productsResponse, settingsResponse, productSettingsResponse] = await Promise.all([
        getProducts({ includeHidden: true }),
        getSettings(),
        getProductSettings(),
      ]);

      setProducts(productsResponse.products as unknown as ShopifyProduct[]);
      setGlobalSettings(settingsResponse.settings);

      // Create map of product settings
      const settingsMap = new Map<string, ProductSettings>();
      for (const ps of productSettingsResponse.productSettings) {
        settingsMap.set(ps.productId, ps);
      }
      setProductSettings(settingsMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleToggleHidden = async (product: ShopifyProduct) => {
    const currentSettings = productSettings.get(product.id);
    const isCurrentlyHidden = currentSettings?.hidden || false;

    try {
      setSavingProduct(product.id);
      await updateProductSetting(product.id, {
        productTitle: product.title,
        hidden: !isCurrentlyHidden,
      });

      // Update local state
      setProductSettings((prev) => {
        const newMap = new Map(prev);
        newMap.set(product.id, {
          ...currentSettings,
          productId: product.id,
          productTitle: product.title,
          hidden: !isCurrentlyHidden,
        });
        return newMap;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update product');
    } finally {
      setSavingProduct(null);
    }
  };

  const handleToggleQtyDiscount = async (product: ShopifyProduct) => {
    const currentSettings = productSettings.get(product.id);
    // Default to true if not set
    const isCurrentlyEnabled = currentSettings?.quantityDiscountEnabled ?? true;

    try {
      setSavingProduct(product.id);
      await updateProductSetting(product.id, {
        productTitle: product.title,
        quantityDiscountEnabled: !isCurrentlyEnabled,
      });

      // Update local state
      setProductSettings((prev) => {
        const newMap = new Map(prev);
        newMap.set(product.id, {
          ...currentSettings,
          productId: product.id,
          productTitle: product.title,
          hidden: currentSettings?.hidden || false,
          quantityDiscountEnabled: !isCurrentlyEnabled,
        });
        return newMap;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update product');
    } finally {
      setSavingProduct(null);
    }
  };

  const handleSaveSettings = async (product: ShopifyProduct, settings: Partial<ProductSettings>) => {
    try {
      setSavingProduct(product.id);
      await updateProductSetting(product.id, {
        ...settings,
        productTitle: product.title,
      });

      // Update local state
      setProductSettings((prev) => {
        const newMap = new Map(prev);
        const current = prev.get(product.id) || { productId: product.id, hidden: false };
        newMap.set(product.id, { ...current, ...settings, productTitle: product.title });
        return newMap;
      });

      setEditingProduct(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update product');
    } finally {
      setSavingProduct(null);
    }
  };

  const handleResetToDefault = async (product: ShopifyProduct) => {
    try {
      setSavingProduct(product.id);
      await deleteProductSetting(product.id);

      // Update local state
      setProductSettings((prev) => {
        const newMap = new Map(prev);
        newMap.delete(product.id);
        return newMap;
      });

      setEditingProduct(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset product settings');
    } finally {
      setSavingProduct(null);
    }
  };

  // Get unique product types
  const productTypes = [...new Set(products.map((p) => p.productType))].sort();

  // Count removed products
  const removedCount = products.filter((p) => productSettings.get(p.id)?.hidden).length;

  // Filter products
  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.variants.some((v) => v.sku?.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = filterType === 'all' || product.productType === filterType;
    const matchesCustom = !showOnlyCustom || productSettings.has(product.id);
    const isHidden = productSettings.get(product.id)?.hidden || false;
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && !isHidden) ||
      (statusFilter === 'removed' && isHidden);
    return matchesSearch && matchesType && matchesCustom && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 border-4 border-workshop-elevated border-t-accent rounded-full"
        />
      </div>
    );
  }

  if (error && !products.length) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">{error}</div>
        <button onClick={fetchData} className="btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-display text-cream tracking-wide">
            Product Management
          </h2>
          <p className="text-sm text-text-muted mt-1">
            Configure per-product pricing and visibility
          </p>
        </div>
        <div className="text-sm text-text-muted">
          {productSettings.size} products with custom settings
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-950/50 border border-red-800/30 rounded-lg text-red-300 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <input
          type="text"
          placeholder="Search products or SKUs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 min-w-[200px] px-4 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
        />
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
        >
          <option value="all">All Categories</option>
          {productTypes.map((type) => (
            <option key={type} value={type}>
              {type || 'Uncategorized'}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'removed')}
          className="px-4 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
        >
          <option value="all">All Status</option>
          <option value="active">Active ({products.length - removedCount})</option>
          <option value="removed">Removed ({removedCount})</option>
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showOnlyCustom}
            onChange={(e) => setShowOnlyCustom(e.target.checked)}
            className="rounded border-white/10"
          />
          Show only custom settings
        </label>
      </div>

      {/* Products Table */}
      <div className="card overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.06] text-left">
              <th className="pb-3 text-xs uppercase tracking-wide text-text-muted font-medium">
                Product
              </th>
              <th className="pb-3 text-xs uppercase tracking-wide text-text-muted font-medium">
                Category
              </th>
              <th className="pb-3 text-xs uppercase tracking-wide text-text-muted font-medium text-center">
                Status
              </th>
              <th className="pb-3 text-xs uppercase tracking-wide text-text-muted font-medium">
                Discount
              </th>
              <th className="pb-3 text-xs uppercase tracking-wide text-text-muted font-medium">
                Qty Discount
              </th>
              <th className="pb-3 text-xs uppercase tracking-wide text-text-muted font-medium text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((product) => {
              const settings = productSettings.get(product.id);
              const hasCustomSettings = !!settings && (
                settings.wholesaleDiscountPercent !== undefined ||
                settings.quantityDiscountThreshold !== undefined ||
                settings.quantityDiscountAmount !== undefined ||
                settings.quantityDiscountEnabled !== undefined ||
                settings.minimumQuantity !== undefined
              );
              const isHidden = settings?.hidden || false;
              const qtyDiscountEnabled = settings?.quantityDiscountEnabled ?? true;
              const isEditing = editingProduct === product.id;
              const isSaving = savingProduct === product.id;

              return (
                <motion.tr
                  key={product.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`border-b border-white/[0.04] last:border-0 ${
                    isHidden ? 'opacity-50' : ''
                  }`}
                >
                  <td className="py-4">
                    <div className="flex items-center gap-3">
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.title}
                          className="w-10 h-10 object-cover rounded"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-white/5 rounded" />
                      )}
                      <div>
                        <div className="font-medium text-cream">{product.title}</div>
                        <div className="text-xs text-text-muted">
                          {product.variants.length} variant(s)
                          {hasCustomSettings && (
                            <span className="ml-2 px-1.5 py-0.5 bg-accent/20 text-accent rounded text-[10px] uppercase">
                              Custom
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 text-sm">
                    {product.productType || <span className="text-text-muted">—</span>}
                  </td>
                  <td className="py-4 text-center">
                    <button
                      onClick={() => handleToggleHidden(product)}
                      disabled={isSaving}
                      className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wide rounded-lg transition-colors ${
                        isHidden
                          ? 'bg-red-900/30 text-red-400 border border-red-800/30 hover:bg-red-900/50'
                          : 'bg-green-900/30 text-green-400 border border-green-800/30 hover:bg-green-900/50'
                      } disabled:opacity-50`}
                    >
                      {isSaving ? 'Saving...' : isHidden ? 'Removed' : 'Active'}
                    </button>
                  </td>
                  <td className="py-4 text-sm font-mono">
                    {settings?.wholesaleDiscountPercent !== undefined
                      ? `${settings.wholesaleDiscountPercent}%`
                      : `${globalSettings?.wholesaleDiscountPercent || 50}%`}
                    {settings?.wholesaleDiscountPercent === undefined && (
                      <span className="text-xs text-text-muted ml-1">(default)</span>
                    )}
                  </td>
                  <td className="py-4 text-sm">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleQtyDiscount(product)}
                        disabled={isSaving}
                        className={`w-8 h-8 rounded-full transition-colors ${
                          qtyDiscountEnabled
                            ? 'bg-green-900/30 text-green-400 hover:bg-green-900/40'
                            : 'bg-red-900/30 text-red-400 hover:bg-red-900/40'
                        } disabled:opacity-50`}
                        title={qtyDiscountEnabled ? 'Disable quantity discount' : 'Enable quantity discount'}
                      >
                        {qtyDiscountEnabled ? (
                          <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                      </button>
                      <span className={`font-mono ${!qtyDiscountEnabled ? 'line-through text-text-muted' : ''}`}>
                        {settings?.quantityDiscountThreshold !== undefined || settings?.quantityDiscountAmount !== undefined ? (
                          <>
                            {settings?.quantityDiscountThreshold ?? globalSettings?.quantityDiscountThreshold}+ = ${(settings?.quantityDiscountAmount ?? globalSettings?.quantityDiscountAmount ?? 0).toFixed(2)} off
                          </>
                        ) : (
                          <>
                            {globalSettings?.quantityDiscountThreshold}+ = ${(globalSettings?.quantityDiscountAmount ?? 0).toFixed(2)} off
                            <span className="text-xs text-text-muted ml-1">(default)</span>
                          </>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleToggleHidden(product)}
                        disabled={isSaving}
                        className={`px-3 py-1 text-xs uppercase tracking-wide rounded transition-colors disabled:opacity-50 ${
                          isHidden
                            ? 'bg-green-900/30 text-green-400 hover:bg-green-900/50 border border-green-800/30'
                            : 'bg-red-900/30 text-red-400 hover:bg-red-900/50 border border-red-800/30'
                        }`}
                      >
                        {isSaving ? '...' : isHidden ? 'Restore' : 'Remove'}
                      </button>
                      <button
                        onClick={() => setEditingProduct(isEditing ? null : product.id)}
                        className="px-3 py-1 text-xs uppercase tracking-wide bg-white/5 text-cream rounded hover:bg-white/10 transition-colors"
                      >
                        {isEditing ? 'Cancel' : 'Edit'}
                      </button>
                    </div>
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Product Edit Modal */}
      {editingProduct && (
        <ProductEditModal
          product={products.find((p) => p.id === editingProduct)!}
          settings={productSettings.get(editingProduct)}
          globalSettings={globalSettings}
          onSave={handleSaveSettings}
          onReset={handleResetToDefault}
          onClose={() => setEditingProduct(null)}
          saving={savingProduct === editingProduct}
        />
      )}
    </div>
  );
}

/**
 * Product Edit Modal Component
 */
function ProductEditModal({
  product,
  settings,
  globalSettings,
  onSave,
  onReset,
  onClose,
  saving,
}: {
  product: ShopifyProduct;
  settings: ProductSettings | undefined;
  globalSettings: GlobalSettings | null;
  onSave: (product: ShopifyProduct, settings: Partial<ProductSettings>) => void;
  onReset: (product: ShopifyProduct) => void;
  onClose: () => void;
  saving: boolean;
}) {
  const [formData, setFormData] = useState({
    wholesaleDiscountPercent: settings?.wholesaleDiscountPercent ?? '',
    quantityDiscountThreshold: settings?.quantityDiscountThreshold ?? '',
    quantityDiscountAmount: settings?.quantityDiscountAmount ?? '',
    quantityDiscountEnabled: settings?.quantityDiscountEnabled ?? true,
    minimumQuantity: settings?.minimumQuantity ?? '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const settingsToSave: Partial<ProductSettings> = {};

    if (formData.wholesaleDiscountPercent !== '') {
      settingsToSave.wholesaleDiscountPercent = Number(formData.wholesaleDiscountPercent);
    }
    if (formData.quantityDiscountThreshold !== '') {
      settingsToSave.quantityDiscountThreshold = Number(formData.quantityDiscountThreshold);
    }
    if (formData.quantityDiscountAmount !== '') {
      settingsToSave.quantityDiscountAmount = Number(formData.quantityDiscountAmount);
    }
    // Always include quantityDiscountEnabled since it has a meaningful default
    settingsToSave.quantityDiscountEnabled = formData.quantityDiscountEnabled;
    if (formData.minimumQuantity !== '') {
      settingsToSave.minimumQuantity = Number(formData.minimumQuantity);
    }

    onSave(product, settingsToSave);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-workshop-surface rounded-lg shadow-xl border border-white/[0.06] max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt={product.title}
                  className="w-12 h-12 object-cover rounded"
                />
              ) : (
                <div className="w-12 h-12 bg-white/5 rounded" />
              )}
              <div>
                <h3 className="font-display text-cream">{product.title}</h3>
                <p className="text-sm text-text-muted">{product.productType}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-text-muted hover:text-cream transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-cream mb-2">
                Wholesale Discount (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                placeholder={`Default: ${globalSettings?.wholesaleDiscountPercent || 50}%`}
                value={formData.wholesaleDiscountPercent}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    wholesaleDiscountPercent: e.target.value,
                  }))
                }
                className="w-full px-4 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
              />
              <p className="text-xs text-text-muted mt-1">
                Leave blank to use global default
              </p>
            </div>

            {/* Quantity Discount Enabled Toggle */}
            <div className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg border border-white/[0.06]">
              <div>
                <label className="block text-sm font-medium text-cream">
                  Quantity Discount Eligible
                </label>
                <p className="text-xs text-text-muted mt-0.5">
                  Enable quantity discounts for this product
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    quantityDiscountEnabled: !prev.quantityDiscountEnabled,
                  }))
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.quantityDiscountEnabled ? 'bg-accent' : 'bg-white/20'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.quantityDiscountEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className={`space-y-4 ${!formData.quantityDiscountEnabled ? 'opacity-50' : ''}`}>
              <div>
                <label className="block text-sm font-medium text-cream mb-2">
                  Quantity Discount Threshold
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  placeholder={`Default: ${globalSettings?.quantityDiscountThreshold || 3}`}
                  value={formData.quantityDiscountThreshold}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      quantityDiscountThreshold: e.target.value,
                    }))
                  }
                  disabled={!formData.quantityDiscountEnabled}
                  className="w-full px-4 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-cream mb-2">
                  Quantity Discount Amount ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder={`Default: $${(globalSettings?.quantityDiscountAmount || 2.5).toFixed(2)}`}
                  value={formData.quantityDiscountAmount}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      quantityDiscountAmount: e.target.value,
                    }))
                  }
                  disabled={!formData.quantityDiscountEnabled}
                  className="w-full px-4 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-cream mb-2">
                Minimum Quantity
              </label>
              <input
                type="number"
                min="1"
                step="1"
                placeholder="Default: 1"
                value={formData.minimumQuantity}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    minimumQuantity: e.target.value,
                  }))
                }
                className="w-full px-4 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
              />
              <p className="text-xs text-text-muted mt-1">
                Minimum quantity required to order this product
              </p>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
              {settings && (
                <button
                  type="button"
                  onClick={() => onReset(product)}
                  disabled={saving}
                  className="px-4 py-2 text-sm text-red-400 hover:text-red-300 disabled:opacity-50"
                >
                  Reset to Defaults
                </button>
              )}
              <div className="flex gap-2 ml-auto">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="px-4 py-2 text-sm border border-white/10 rounded-lg hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm bg-accent text-workshop rounded-lg hover:bg-accent-light disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
